import * as types from '../types';
import { IReportsApi } from '../contracts';
import { HttpApi } from './HttpApi';

type ReportParamValue = string | number | null | undefined;

/**
 * Drop blank filters so the backend sees only what the user chose. Axios
 * already omits `undefined`, but it would send `''` (what an empty
 * URLSearchParams value or cleared input gives us) and `null`.
 */
export function compactReportParams(query: object = {}): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(query as Record<string, ReportParamValue>).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    params[key] = value;
  });
  return params;
}

export class ReportsHttpApi extends HttpApi implements IReportsApi {
  private async getReport<T>(url: string, query?: object): Promise<T> {
    const response = await this.http.get<types.APIResponse<T>>(url, { params: compactReportParams(query) });
    return response.data.data;
  }

  getTrialBalance(query?: types.TrialBalanceQuery): Promise<types.TrialBalanceReport> {
    return this.getReport('/reports/trial-balance', query);
  }

  getARAging(query?: types.ARAgingQuery): Promise<types.ARAgingReport> {
    return this.getReport('/reports/ar-aging', query);
  }

  getRevenueReport(query?: types.RevenueReportQuery): Promise<types.RevenueReport> {
    return this.getReport('/reports/revenue', query);
  }

  getJobReport(query?: types.JobReportQuery): Promise<types.JobReport> {
    return this.getReport('/reports/jobs', query);
  }

  getPaymentReport(query?: types.PaymentReportQuery): Promise<types.PaymentReport> {
    return this.getReport('/reports/payments', query);
  }

  getCustomerStatement(customerId: string, query?: types.CustomerStatementQuery): Promise<types.CustomerStatementReport> {
    return this.getReport(`/reports/customers/${encodeURIComponent(customerId)}/statement`, query);
  }
}
